import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { DateTime } from "luxon";
import { toast } from "react-toastify";
import Icon from "@mdi/react";
import {
    mdiAccountEdit,
    mdiAccountGroup,
    mdiCalendarEdit,
    mdiChevronDoubleLeft,
    mdiChevronDoubleRight,
    mdiHelpCircle,
    mdiHelpCircleOutline,
} from "@mdi/js";
import { useAuth } from "../../contexts/AuthContext";
import { bookingApi } from "../../api/booking";
import { availabilityApi } from "../../api/availability";
import { instructorApi } from "../../api/instructor";
import CalendarView from "../../components/CalendarView";
import BookingWeekView from "../../components/BookingWeekView";
import MessagesView from "../../components/MessagesView";
import BookingDetailModal from "../../components/BookingDetailModal";
import RejectionModal from "../../components/RejectionModal";
import CancellationModal from "../../components/CancellationModal";
import SickDayConfirmModal from "../../components/SickDayConfirmModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import AppHeader from "../../components/AppHeader";
import InstructorDashboardHelpModal from "./InstructorDashboardHelpModal";
import "./InstructorDashboard.css";
import "../header.css";
import "../Common.css";
import "../Dashboard.css";

const SPLIT_VIEW_DAYS = 4; // Tune this to adjust days shown in split layout
const MOBILE_VIEW_DAYS = 3; // Phones: 3 columns regardless of month collapse
// Phones only: data-dense pages break at 600px so iPads keep the desktop layout
// (docs/WEB-STYLING-GUIDE.md, iPad Breakpoint Philosophy). Matches the 600px blocks in
// InstructorDashboard.css, BookingWeekView.css and ManageBookingsHeader.css.
const MOBILE_QUERY = "(max-width: 600px)";

const InstructorDashboard = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    // Calendar state
    const [currentMonth, setCurrentMonth] = useState(DateTime.local());
    const [selectedDate, setSelectedDate] = useState(DateTime.local());

    // The loaded data window — bookings + availability are fetched for the viewed
    // month padded a few weeks back and one week forward. The back padding lets you
    // scroll a reasonable amount into the past without paging months (and without
    // pulling all history up front). The week view's arrows clamp to this window; at
    // the edge they auto-page the month calendar (which loads the next chunk on
    // demand), so data and navigation can never disagree. Single source of truth for
    // both the fetch range and the week-view nav bounds.
    // Re-entering an already-visited month refetches it (no month cache) — deliberate:
    // rapid month-to-month scrolling is a rare edge case (e.g. chasing a missed
    // booking), so the per-step fetch cost isn't worth a cache.
    const PAST_PADDING_WEEKS = 4;
    const loadedWindow = useMemo(
        () => ({
            start: currentMonth.startOf("month").minus({ weeks: PAST_PADDING_WEEKS }).startOf("day"),
            end: currentMonth.endOf("month").plus({ weeks: 1 }).endOf("day"),
        }),
        [currentMonth]
    );
    const [availabilitySlots, setAvailabilitySlots] = useState([]);
    const [travelPeriods, setTravelPeriods] = useState([]);
    const [blockedDates, setBlockedDates] = useState([]);
    const [sickDays, setSickDays] = useState([]);

    // Booking state
    const [bookings, setBookings] = useState([]);
    const [dayBookings, setDayBookings] = useState([]);
    const [filterStatus] = useState("all"); // 'all' | 'pending' | 'confirmed'

    // UI state
    // isLoading is the FIRST-LOAD gate only — it swaps the whole content area for a
    // "Loading dashboard..." card. Month-change refetches must NOT flip it, or both
    // calendars unmount and the section visibly reloads. Those use isRefetching, a
    // lightweight overlay that keeps the calendars mounted.
    const [isLoading, setIsLoading] = useState(true);
    const [isRefetching, setIsRefetching] = useState(false);
    const hasLoadedOnce = useRef(false);
    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [viewMode, setViewMode] = useState("bookings"); // 'bookings' | 'messages'
    const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches);
    // Phones open bookings-first: the month overview stacks above the bookings, so it
    // starts hidden and "Show Month" brings it back on demand.
    const [calendarCollapsed, setCalendarCollapsed] = useState(() => window.matchMedia(MOBILE_QUERY).matches);

    useEffect(() => {
        const mql = window.matchMedia(MOBILE_QUERY);
        const onChange = e => setIsMobile(e.matches);
        mql.addEventListener("change", onChange);
        return () => mql.removeEventListener("change", onChange);
    }, []);

    const weekViewDays = isMobile ? MOBILE_VIEW_DAYS : calendarCollapsed ? 7 : SPLIT_VIEW_DAYS;

    const [helpOpen, setHelpOpen] = useState(false);
    const [helpIconHovered, setHelpIconHovered] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showRejectionModal, setShowRejectionModal] = useState(false);
    const [bookingToReject, setBookingToReject] = useState(null);
    const [showCancellationModal, setShowCancellationModal] = useState(false);
    const [bookingToCancel, setBookingToCancel] = useState(null);
    const [instructorProfile, setInstructorProfile] = useState(null);

    // Sick day state
    const [showSickDayModal, setShowSickDayModal] = useState(false);
    const [isSickDayProcessing, setIsSickDayProcessing] = useState(false);
    const [todaysBookings, setTodaysBookings] = useState([]);

    // Confirm-all confirmation modal state: { count, unpaidBeforeCount } | null
    const [confirmAllInfo, setConfirmAllInfo] = useState(null);
    const [approveWarningBooking, setApproveWarningBooking] = useState(null);

    // Load data on mount and when month changes
    useEffect(() => {
        if (user?.organisationId) {
            loadDashboardData();
        }
    }, [user, currentMonth]);

    // Filter bookings when selected date changes
    useEffect(() => {
        filterBookingsByDate();
    }, [selectedDate, bookings, filterStatus]);

    // Filter travel periods to only show those in the current month
    const getVisibleTravelPeriods = () => {
        if (!currentMonth || travelPeriods.length === 0) return [];

        const monthStart = currentMonth.startOf("month");
        const monthEnd = currentMonth.endOf("month");

        return travelPeriods.filter(period => {
            const periodStart = DateTime.fromISO(period.startDate);
            const periodEnd = DateTime.fromISO(period.endDate);

            // Check if period overlaps with current month
            return periodStart <= monthEnd && periodEnd >= monthStart;
        });
    };

    // Update selectedBooking when bookings refresh (keeps modal open with fresh data)
    useEffect(() => {
        if (selectedBooking && showModal && bookings.length > 0) {
            const updatedBooking = bookings.find(b => b.id === selectedBooking.id);
            if (updatedBooking) {
                setSelectedBooking(updatedBooking);
            }
        }
    }, [bookings]); // Only depend on bookings array changes

    // Show toast notifications for success/error messages
    useEffect(() => {
        if (successMessage) {
            toast.success(successMessage);
        }
        if (error) {
            toast.error(error);
        }
    }, [successMessage, error]);

    const loadDashboardData = async () => {
        // First load shows the full-section "Loading dashboard..." card; every
        // subsequent refetch (month paging, post-mutation refresh) keeps the
        // calendars mounted and shows only the lightweight overlay.
        const isFirstLoad = !hasLoadedOnce.current;
        try {
            if (isFirstLoad) setIsLoading(true);
            else setIsRefetching(true);
            setError("");

            // Calculate date range for current month
            const monthStart = currentMonth.startOf("month").toISODate();
            const monthEnd = currentMonth.endOf("month").toISODate();

            // Bookings are scoped to the VIEWED month (loadedWindow), same as the
            // availability calendar — both re-fetch when currentMonth changes (see the
            // [user, currentMonth] effect), so advancing the calendar loads each month's
            // bookings on demand instead of pulling all history/future up front (which made
            // load slow once dozens of bookings accumulated). Deep history lives in the DB
            // and surfaces via the student history view.
            const bookingsStart = loadedWindow.start.toISODate();
            const bookingsEnd = loadedWindow.end.toISODate();

            // Load all data in parallel
            const [calendar, periods, allBookings, profile, contexts, overrides] = await Promise.all([
                availabilityApi.getMergedCalendar(user.organisationId, monthStart, monthEnd),
                availabilityApi.getTravelPeriods(user.organisationId).catch(() => []),
                bookingApi.getInstructorBookings({ startDate: bookingsStart, endDate: bookingsEnd }),
                instructorApi.getProfile(user.organisationId).catch(() => null),
                availabilityApi.getContexts().catch(() => []),
                availabilityApi
                    .getOverrides(user.organisationId, { startDate: monthStart, endDate: monthEnd })
                    .catch(() => []),
            ]);

            // Transform merged calendar to availability slots array
            const slots = Object.entries(calendar).flatMap(([date, daySlots]) =>
                daySlots.map(slot => ({ ...slot, date }))
            );

            const blocked = contexts
                .filter(ctx => ctx.context === "holiday" || ctx.context === "leave" || ctx.context === "personal")
                .map(ctx => ({
                    id: ctx.id,
                    startDate: ctx.startDate,
                    endDate: ctx.endDate,
                    context: ctx.context,
                    reason: ctx.reason || "",
                    color: ctx.color || "#999999",
                }));

            const sick = overrides
                .filter(o => o.isClosed && o.source === "sick_day")
                .map(o => ({
                    id: o.id,
                    date: DateTime.fromISO(o.date).toISODate(),
                    reason: o.reason || "",
                }));

            setAvailabilitySlots(slots);
            setTravelPeriods(periods);
            setBlockedDates(blocked);
            setSickDays(sick);
            setBookings(allBookings);
            setInstructorProfile(profile);
        } catch (error) {
            console.error("Error loading dashboard data:", error);
            setError("Failed to load dashboard. Please try again.");
        } finally {
            hasLoadedOnce.current = true;
            setIsLoading(false);
            setIsRefetching(false);
        }
    };

    // Paging the month calendar also moves the selected date into that month, so the
    // week view (which derives its week from selectedDate) follows the loaded window
    // instead of being stranded on the previous month's week. Land on today when
    // paging to the current month, otherwise the 1st of the target month.
    const handleMonthChange = newMonth => {
        setCurrentMonth(newMonth);
        const today = DateTime.local();
        const isCurrentMonth = newMonth.hasSame(today, "month") && newMonth.hasSame(today, "year");
        setSelectedDate(isCurrentMonth ? today : newMonth.startOf("month"));
    };

    // "Today" resets BOTH views: the month calendar jumps to today's month (which
    // re-fetches that month's data window) and the week view lands on today. Without
    // moving currentMonth too, the week could jump to a date outside the loaded window.
    const handleGoToToday = () => {
        const today = DateTime.local();
        setCurrentMonth(today);
        setSelectedDate(today);
    };

    // The week-view arrows clamp to the loaded data window. When you page past that
    // edge we don't dead-end — we move the month calendar one month in that direction
    // (which loads its data) and land the week on the days immediately adjacent to the
    // window we just left, so week + month stay in sync and paging feels continuous.
    // The week view derives its start from selectedDate and shows forward, so for
    // "back" we anchor selectedDate so the *last* shown day sits just before the old
    // window start (revealing earlier days); for "forward" the first day is just after.
    const handleWeekCrossWindow = direction => {
        const step = direction === "back" ? -1 : 1;
        const newMonth = currentMonth.plus({ months: step });
        setCurrentMonth(newMonth);
        const span = weekViewDays;
        const target =
            direction === "back"
                ? loadedWindow.start.minus({ days: span }) // last shown day = old window start − 1
                : loadedWindow.end.plus({ days: 1 }); // first shown day = old window end + 1
        setSelectedDate(target.startOf("day"));
    };

    const filterBookingsByDate = () => {
        const dateString = selectedDate.toISODate();

        let filtered = bookings.filter(booking => {
            const bookingDate = booking.bookingDate.split("T")[0];
            return bookingDate === dateString;
        });

        // Apply status filter
        if (filterStatus === "pending") {
            filtered = filtered.filter(b => b.status === "pending");
        } else if (filterStatus === "confirmed") {
            filtered = filtered.filter(b => b.status === "confirmed");
        }

        // Sort by time
        filtered.sort((a, b) => {
            return a.timeSlot.startTime.localeCompare(b.timeSlot.startTime);
        });

        setDayBookings(filtered);
    };

    const handleApproveBooking = async bookingId => {
        // Before-lesson bookings are normally confirmed by the student paying. Approving
        // an unpaid one is allowed, but only with informed consent: the instructor takes
        // on collecting payment themselves (e.g. via Request Payment).
        const booking = bookings.find(b => b.id === bookingId);
        if (
            booking &&
            booking.pricing?.paymentTiming === "before-lesson" &&
            booking.pricing?.paymentStatus !== "paid"
        ) {
            setApproveWarningBooking(booking);
            return;
        }
        await doApproveBooking(bookingId);
    };

    const doApproveBooking = async bookingId => {
        // Arm the blocking overlay synchronously, BEFORE the async confirm. The detail
        // modal closes itself the instant this is called, so if we waited for
        // loadDashboardData() to flip isRefetching (after the confirm round-trip) there
        // would be a network-latency gap where the modal is gone but the week view is
        // still interactive — letting the user open stale cards mid-refresh.
        setIsRefetching(true);
        try {
            setError("");
            setSuccessMessage("");

            await bookingApi.confirmBooking(bookingId);
            setSuccessMessage("Booking approved successfully!");

            // Reload all dashboard data (bookings + availability calendar).
            // Its finally clears isRefetching on success.
            await loadDashboardData();

            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error("Error approving booking:", error);
            setError(error.message || "Failed to approve booking");
            setIsRefetching(false); // confirm threw — loadDashboardData never ran to clear it
        }
    };

    const handleRejectBooking = async bookingId => {
        // Find the booking to reject
        const booking = bookings.find(b => b.id === bookingId);
        if (!booking) {
            setError("Booking not found");
            return;
        }

        // Show rejection modal
        setBookingToReject(booking);
        setShowRejectionModal(true);
    };

    const confirmRejectBooking = async (bookingId, reason) => {
        try {
            setError("");
            setSuccessMessage("");

            await bookingApi.rejectBooking(bookingId, reason);
            setSuccessMessage("Booking rejected and message sent to student");

            // Close both modals (rejection modal and booking detail modal)
            setShowRejectionModal(false);
            setBookingToReject(null);
            setShowModal(false);
            setSelectedBooking(null);

            // Reload all dashboard data (bookings + availability calendar)
            await loadDashboardData();

            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error("Error rejecting booking:", error);
            setError(error.message || "Failed to reject booking");
            throw error; // Re-throw so modal knows to keep showing
        }
    };

    /**
     * Handle booking time update
     * @param {string} bookingId - Booking ID
     * @param {object} timeData - { startTime, endTime, nudgeFollowing?, bookingShifts?, slotShifts? }
     */
    const handleUpdateBookingTime = async (bookingId, timeData) => {
        try {
            setError("");
            setSuccessMessage("");

            if (timeData.nudgeFollowing) {
                // Use nudge endpoint - applies the explicit booking + slot shifts the modal computed.
                // Strip routing flag and location (nudge endpoint doesn't support location changes)
                const { nudgeFollowing, location, ...nudgePayload } = timeData;
                const result = await bookingApi.nudgeBookingTime(bookingId, nudgePayload);

                setSuccessMessage(
                    result.shiftedCount > 0
                        ? `Booking time updated. ${result.shiftedCount} following booking(s) shifted. Affected students have been notified.`
                        : "Booking time updated. The student has been notified."
                );
            } else {
                // Use simple update endpoint
                // Strip routing flag before sending to backend
                const { nudgeFollowing, ...updatePayload } = timeData;
                await bookingApi.updateBooking(bookingId, updatePayload);
                setSuccessMessage("Booking time updated. The student has been notified.");
            }

            // Reload all dashboard data (bookings + availability calendar)
            await loadDashboardData();

            // Close modal
            setShowModal(false);
            setSelectedBooking(null);

            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error("Error updating booking time:", error);
            setError(error.message || "Failed to update booking time");
            setTimeout(() => setError(""), 3000);
        }
    };

    /**
     * Handle booking cancellation (instructor side)
     * Opens CancellationModal for confirmation and optional reason
     */
    const handleCancelBooking = async bookingId => {
        // Find the booking object
        const booking = bookings.find(b => b.id === bookingId);
        if (!booking) {
            setError("Booking not found");
            return;
        }

        // Show cancellation modal
        setBookingToCancel(booking);
        setShowCancellationModal(true);
    };

    /**
     * Confirm cancellation from modal
     */
    const handleConfirmCancellation = async (bookingId, reason) => {
        setError("");
        setSuccessMessage("");

        await bookingApi.cancelBooking(bookingId, reason);
        // Errors propagate to CancellationModal's catch (which keeps its confirm
        // step open) — swallowing them here made the modal show success toasts
        // for a failed cancel.

        // Close the detail modal IMMEDIATELY after the cancel succeeds — before the
        // data refresh. A refresh failure must never strand a stale BookingDetailModal
        // with active buttons on a now-cancelled booking (E2E round 3, S8).
        setShowModal(false);
        setSelectedBooking(null);
        setSuccessMessage("Booking cancelled successfully");
        setTimeout(() => setSuccessMessage(""), 3000);

        // Reload all dashboard data (bookings + availability calendar) — non-fatal
        try {
            await loadDashboardData();
        } catch (refreshError) {
            console.error("Post-cancel dashboard refresh failed:", refreshError);
        }
    };

    /**
     * Handle message sent or booking created - refresh bookings and availability
     */
    const handleMessageSent = async bookingId => {
        // Reload full dashboard data to update bookings AND availability slots
        // This ensures availability slots disappear when manual bookings are created
        try {
            await loadDashboardData();
        } catch (error) {
            console.error("Error refreshing dashboard:", error);
        }
    };

    /**
     * Handle messages marked as read - update unreadCount locally without a full reload
     */
    const handleMessagesRead = bookingId => {
        setBookings(prev => prev.map(b => (b.id === bookingId ? { ...b, unreadCount: 0 } : b)));
    };

    // Gate for the header "Confirm" (confirm-all) button: always show a confirmation
    // with the pending count, plus a heads-up when the batch includes unpaid
    // before-lesson bookings (those are normally confirmed by the student paying).
    // Scope: all UPCOMING pending bookings (today onwards) across the loaded window.
    // Past-dated pendings are excluded — bulk-confirming a lesson whose date already
    // passed would email the student a confirmation for a lesson that never happened.
    const upcomingPendingFilter = b =>
        b.status === "pending" && b.bookingDate.split("T")[0] >= DateTime.local().toISODate();

    const handleConfirmAllPending = () => {
        const pendingBookings = bookings.filter(upcomingPendingFilter);

        if (pendingBookings.length === 0) {
            setError("No pending bookings");
            setTimeout(() => setError(""), 3000);
            return;
        }

        setConfirmAllInfo({
            count: pendingBookings.length,
            bookings: pendingBookings,
            unpaidBeforeCount: pendingBookings.filter(
                b => b.pricing?.paymentTiming === "before-lesson" && b.pricing?.paymentStatus !== "paid"
            ).length,
        });
    };

    const doConfirmAllPending = async () => {
        const pendingBookings = bookings.filter(upcomingPendingFilter);

        if (pendingBookings.length === 0) {
            setError("No pending bookings");
            setTimeout(() => setError(""), 3000);
            return;
        }

        try {
            setError("");
            setSuccessMessage("");

            // Confirm all bookings in parallel
            await Promise.all(pendingBookings.map(b => bookingApi.confirmBooking(b.id)));

            setSuccessMessage(`Confirmed ${pendingBookings.length} booking(s)`);

            // Reload all dashboard data (bookings + availability calendar)
            await loadDashboardData();

            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error("Error confirming bookings:", error);
            setError("Failed to confirm all bookings");
            setTimeout(() => setError(""), 3000);
        }
    };

    const handleOpenModal = booking => {
        // Ignore card clicks while a load/refetch is in flight — defends against opening
        // a stale booking in the brief window after a mutation closes its modal.
        if (isLoading || isRefetching) return;
        setSelectedBooking(booking);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedBooking(null);

        // Refresh data when closing modal from messages view
        // This ensures message counts update after marking messages as read
        if (viewMode === "messages") {
            loadDashboardData();
        }
    };

    /**
     * Active bookings for a given ISO date, formatted for the sick day modal.
     * Includes BOTH unpaid and paid bookings — the modal cancels unpaid ones with the
     * sick day and requires a per-booking refund/credit/keep decision for paid ones
     * (sick day is a block, not a refund path). Paid-ness is pricing.paymentStatus /
     * creditApplied, NOT status. Sorted by start time (earliest first).
     */
    const getSickDayBookingsForDate = dateIso => {
        return bookings
            .filter(booking => {
                const bookingDate = booking.bookingDate.split("T")[0];
                return bookingDate === dateIso && ["pending", "confirmed"].includes(booking.status);
            })
            .map(booking => {
                const isPaid =
                    ["paid", "refunded", "credited"].includes(booking.pricing?.paymentStatus) ||
                    (booking.pricing?.creditApplied || 0) > 0;
                const ep = booking.effectivePricing || {};
                return {
                    id: booking.id,
                    status: booking.status,
                    startTime: booking.timeSlot.startTime,
                    studentName: booking.studentName,
                    studentEmail: booking.studentEmail,
                    isPaid,
                    isGuest: booking.studentProfileId === "000000000000000000000000",
                    refundableAmount: (ep.lessonFee || 0) + (ep.venueFee || 0),
                    currency: ep.currency || booking.pricing?.currency || "AUD",
                };
            })
            .sort((a, b) => a.startTime.localeCompare(b.startTime));
    };

    /**
     * Sick day initial date: the dashboard's selected day, clamped to today.
     * The dashboard deliberately allows selecting PAST days (payment requests etc.),
     * but a sick day can't be recorded in the past — so a past selection falls back
     * to today. The modal's own picker also blocks past dates.
     */
    const getSickDayInitialDate = () => {
        const today = DateTime.local().startOf("day");
        return (selectedDate < today ? today : selectedDate).toISODate();
    };

    /**
     * Handle sick day button click
     * Opens modal showing the initial date's bookings that will be cancelled
     */
    const handleSickDay = () => {
        // User can change the date in the modal (which re-derives the list via onDateChange)
        setTodaysBookings(getSickDayBookingsForDate(getSickDayInitialDate()));
        setShowSickDayModal(true);
    };

    /**
     * Confirm sick day from modal. Processes paid-booking refund/credit decisions first
     * (via issueReturn as a FULL return, which cancels the booking), then blocks the day
     * and cancels the unpaid bookings. 'keep' leaves a paid booking live by the
     * instructor's choice. See ManageAvailabilityUnified.handleConfirmSickDay for the
     * canonical version of this flow.
     *
     * @param {Array<{bookingId: string, action: 'refund'|'credit'|'keep'}>} paidDecisions
     */
    const handleConfirmSickDay = async (cancellationMessage, selectedBookingIds, date, paidDecisions = []) => {
        try {
            setIsSickDayProcessing(true);
            setError("");
            setSuccessMessage("");

            // 1. Paid-booking refund/credit (full return → cancels booking). 'keep' is a no-op.
            let returnsIssued = 0;
            const returnFailures = [];
            for (const decision of paidDecisions) {
                if (decision.action !== "refund" && decision.action !== "credit") continue;
                try {
                    await bookingApi.issueReturn(decision.bookingId, {
                        type: decision.action,
                        message: cancellationMessage,
                    });
                    returnsIssued += 1;
                } catch (err) {
                    console.error(`Sick day: failed to ${decision.action} booking ${decision.bookingId}:`, err);
                    returnFailures.push(decision.action);
                }
            }

            // 2. Block the day + cancel the unpaid bookings.
            await availabilityApi.sickDay({
                date,
                reason: cancellationMessage,
                bookingIds: selectedBookingIds,
            });

            // Close modal
            setShowSickDayModal(false);

            const parts = ["Sick day recorded"];
            if (selectedBookingIds.length > 0) parts.push(`${selectedBookingIds.length} booking(s) cancelled`);
            if (returnsIssued > 0) parts.push(`${returnsIssued} refund/credit issued`);
            if (selectedBookingIds.length === 0 && returnsIssued === 0) parts.push("availability blocked");
            const message = `${parts.join(", ")}.`;

            if (returnFailures.length > 0) {
                setError(
                    `${message} But ${returnFailures.length} refund/credit failed. Resolve those bookings individually.`
                );
                setTimeout(() => setError(""), 6000);
            } else {
                setSuccessMessage(message);
                setTimeout(() => setSuccessMessage(""), 5000);
            }

            // Reload dashboard data
            await loadDashboardData();
        } catch (error) {
            console.error("Error recording sick day:", error);
            setError(error.message || "Failed to record sick day");
            setTimeout(() => setError(""), 5000);
        } finally {
            setIsSickDayProcessing(false);
        }
    };

    return (
        <div className="dashboard-container org-console instructor-dashboard-page">
            {/* Standard Navigation Header */}
            <AppHeader />
            <div className="dashboard-main">
                {/* Portal Header Card */}
                <div className="dashboard-content-wrapper">
                    <div className="sub-portal-header-card" style={{ padding: "12px 20px" }}>
                        {/* Row 1: back button + compact nav cards inline */}
                        <div className="header-nav-row">
                            <button onClick={() => navigate("/dashboard")} className="back-nav-btn">
                                ← Dashboard
                            </button>
                            <Link to="/instructor-profile-setup/profile" className="calendar-nav-card">
                                <div className="calendar-nav-card-header">
                                    <Icon path={mdiAccountEdit} size={0.9} color="var(--dl-color-purple-default)" />
                                    <h3 className="calendar-nav-card-title">Set Up Your Booking Profile</h3>
                                    <span className="calendar-nav-step-badge">1</span>
                                </div>
                                <p className="calendar-nav-card-description">Location, pricing & about you</p>
                            </Link>
                            <Link to="/manage-availability" className="calendar-nav-card">
                                <div className="calendar-nav-card-header">
                                    <Icon path={mdiCalendarEdit} size={0.9} color="var(--dl-color-purple-default)" />
                                    <h3 className="calendar-nav-card-title">Set Your Availability</h3>
                                    <span className="calendar-nav-step-badge">2</span>
                                </div>
                                <p className="calendar-nav-card-description">
                                    Set your teaching schedule and availability
                                </p>
                            </Link>
                            <Link to="/bookings/students" className="calendar-nav-card">
                                <div className="calendar-nav-card-header">
                                    <Icon path={mdiAccountGroup} size={0.9} color="var(--dl-color-purple-default)" />
                                    <h3 className="calendar-nav-card-title">My Students</h3>
                                </div>
                                <p className="calendar-nav-card-description">View and manage your booking students</p>
                            </Link>
                        </div>

                        {/* Row 2: subtitle + help trigger */}
                        <div className="instructor-dashboard-subtitle-row">
                            <p className="sub-portal-header-subtitle">Set your availability and manage bookings</p>
                            <button
                                className="classes-help-trigger"
                                onClick={() => setHelpOpen(true)}
                                onMouseEnter={() => setHelpIconHovered(true)}
                                onMouseLeave={() => setHelpIconHovered(false)}
                                aria-label="Instructor Dashboard Help"
                                title="How It Works"
                            >
                                <Icon
                                    path={helpIconHovered ? mdiHelpCircle : mdiHelpCircleOutline}
                                    size="1.62rem"
                                    color="var(--dl-color-purple-default)"
                                />
                            </button>
                        </div>
                    </div>
                </div>

                {isLoading ? (
                    <div className="portal-header-card dashboard-loading-card">
                        <span className="dashboard-refetch-spinner" />
                        <p className="dashboard-loading-text">Loading dashboard...</p>
                    </div>
                ) : (
                    <div className={`portal-dashboard-content${isRefetching ? " is-refetching" : ""}`}>
                        {/* Background refetch (month paging / post-mutation) — keep both calendars
                            mounted and show a non-blocking overlay so the section never reloads. */}
                        {isRefetching && (
                            <div className="dashboard-refetch-overlay" aria-hidden="true">
                                <div className="dashboard-refetch-spinner-box">
                                    <span className="dashboard-refetch-spinner" />
                                    <span className="dashboard-refetch-spinner-label">Loading...</span>
                                </div>
                            </div>
                        )}
                        {/* LEFT BOX: Monthly Overview */}
                        <div className={`schedule-summary-box ${calendarCollapsed ? "collapsed" : ""}`}>
                            {/* Title row */}
                            <div className="box-title-row">
                                <h2 className="box-title">Monthly Overview</h2>
                            </div>

                            {/* Legend — row 1: availability key */}
                            <div className="schedule-legend-availability">
                                <span className="schedule-legend-section-label">Key</span>
                                <div className="schedule-legend-item">
                                    <div className="schedule-legend-badge badge--available"></div>
                                    <span>Available</span>
                                </div>
                                <div className="schedule-legend-item">
                                    <div className="schedule-legend-badge badge--partial"></div>
                                    <span>Partially Booked</span>
                                </div>
                                <div className="schedule-legend-item">
                                    <div className="schedule-legend-badge badge--booked"></div>
                                    <span>Fully Booked</span>
                                </div>
                            </div>
                            {/* Legend — row 2: travel pills (only if any) */}
                            {getVisibleTravelPeriods().length > 0 && (
                                <div className="schedule-legend-availability">
                                    <span className="schedule-legend-section-label">Travel</span>
                                    {getVisibleTravelPeriods().map(period => {
                                        const color = period.color;
                                        return (
                                            <span
                                                key={period.id}
                                                className="schedule-legend-travel-pill"
                                                style={{
                                                    backgroundColor: `${color}20`,
                                                    border: `1.5px solid ${color}`,
                                                }}
                                            >
                                                {period.location?.description || period.destination}
                                            </span>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="schedule-legend-divider" />

                            {/* Calendar — fills full width */}
                            <CalendarView
                                events={[]}
                                compact={false}
                                viewType="monthly"
                                currentDate={currentMonth}
                                onPreviousMonth={newDate => handleMonthChange(newDate)}
                                onNextMonth={newDate => handleMonthChange(newDate)}
                                availabilityMode={true}
                                miniMode={true}
                                availabilitySlots={availabilitySlots}
                                bookings={bookings}
                                onSlotClick={date => setSelectedDate(DateTime.fromISO(date))}
                                selectedDateProp={selectedDate?.toISODate()}
                                isMobile={false}
                                weekStartDay={7}
                                travelPeriods={travelPeriods}
                                blockedDates={blockedDates}
                                sickDays={sickDays}
                                // Past months/weeks are reachable here so instructors can review recent
                                // bookings; the loaded-window padding bounds how much data that pulls.
                                disablePastDates={false}
                            />
                        </div>

                        {/* RIGHT BOX: Dual-Purpose (Bookings or Messages) */}
                        <div className="manage-bookings-box">
                            <div className="box-title-row">
                                {!calendarCollapsed && (
                                    <button
                                        className="week-view-link"
                                        onClick={() => setCalendarCollapsed(true)}
                                        aria-label={
                                            viewMode === "bookings"
                                                ? isMobile
                                                    ? "Hide month view"
                                                    : "Switch to full week view"
                                                : "Expand messages to full width"
                                        }
                                    >
                                        <Icon path={mdiChevronDoubleLeft} size={0.75} />
                                        {viewMode === "bookings"
                                            ? isMobile
                                                ? "Hide Month"
                                                : "Full Week"
                                            : "Expand Messages"}
                                    </button>
                                )}
                                {calendarCollapsed && (
                                    <button
                                        className="week-view-link"
                                        onClick={() => setCalendarCollapsed(false)}
                                        aria-label={viewMode === "bookings" ? "Show month view" : "Collapse messages"}
                                    >
                                        <Icon path={mdiChevronDoubleRight} size={0.75} />
                                        {viewMode === "bookings" ? "Show Month" : "Collapse"}
                                    </button>
                                )}
                                <h2 className="box-title">
                                    {viewMode === "bookings" ? "Manage Bookings" : "Manage Messages"}
                                </h2>
                            </div>

                            {viewMode === "bookings" ? (
                                calendarCollapsed ? (
                                    <BookingWeekView
                                        selectedDate={selectedDate}
                                        bookings={bookings}
                                        availabilitySlots={availabilitySlots}
                                        travelPeriods={travelPeriods}
                                        blockedDates={blockedDates}
                                        sickDays={sickDays}
                                        instructorProfile={instructorProfile}
                                        instructorOrgId={user?.organisationId}
                                        currentUserId={user?.accountId}
                                        onConfirmBooking={handleApproveBooking}
                                        onRejectBooking={handleRejectBooking}
                                        onMessageBooking={handleOpenModal}
                                        onSelectBooking={handleOpenModal}
                                        onUpdateTime={handleUpdateBookingTime}
                                        onCancel={handleCancelBooking}
                                        onSelectDate={setSelectedDate}
                                        onRefresh={loadDashboardData}
                                        onAddManualBooking={() => {}}
                                        onConfirmAll={handleConfirmAllPending}
                                        onOpenMessages={() => setViewMode("messages")}
                                        onSickDay={handleSickDay}
                                        onMessagesRead={handleMessagesRead}
                                        onCrossWindow={handleWeekCrossWindow}
                                        onGoToToday={handleGoToToday}
                                        visibleDays={weekViewDays}
                                        windowStart={loadedWindow.start}
                                        windowEnd={loadedWindow.end}
                                    />
                                ) : (
                                    <BookingWeekView
                                        selectedDate={selectedDate}
                                        bookings={bookings}
                                        availabilitySlots={availabilitySlots}
                                        travelPeriods={travelPeriods}
                                        blockedDates={blockedDates}
                                        sickDays={sickDays}
                                        instructorProfile={instructorProfile}
                                        instructorOrgId={user?.organisationId}
                                        currentUserId={user?.accountId}
                                        onConfirmBooking={handleApproveBooking}
                                        onRejectBooking={handleRejectBooking}
                                        onMessageBooking={handleOpenModal}
                                        onSelectBooking={handleOpenModal}
                                        onUpdateTime={handleUpdateBookingTime}
                                        onCancel={handleCancelBooking}
                                        onSelectDate={setSelectedDate}
                                        onRefresh={loadDashboardData}
                                        onAddManualBooking={() => {}}
                                        onConfirmAll={handleConfirmAllPending}
                                        onOpenMessages={() => setViewMode("messages")}
                                        onSickDay={handleSickDay}
                                        onMessagesRead={handleMessagesRead}
                                        onCrossWindow={handleWeekCrossWindow}
                                        onGoToToday={handleGoToToday}
                                        visibleDays={weekViewDays}
                                        windowStart={loadedWindow.start}
                                        windowEnd={loadedWindow.end}
                                    />
                                )
                            ) : (
                                <MessagesView
                                    currentUserId={user?.accountId}
                                    travelPeriods={travelPeriods}
                                    onBackToBookings={() => setViewMode("bookings")}
                                    onRefresh={loadDashboardData}
                                    onMessageSent={handleMessageSent}
                                    onMessagesRead={handleMessagesRead}
                                    onViewBookingDetails={booking => {
                                        setSelectedBooking(booking);
                                        setShowModal(true);
                                    }}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>{" "}
            {/* End dashboard-main */}
            {/* Booking Detail Modal */}
            {showModal && selectedBooking && (
                <BookingDetailModal
                    booking={selectedBooking}
                    allBookings={bookings}
                    availabilitySlots={availabilitySlots}
                    instructorLocations={instructorProfile?.instructorProfile?.preferredLocations || []}
                    travelPeriods={travelPeriods}
                    currentUserId={user?.accountId}
                    onClose={handleCloseModal}
                    onConfirm={handleApproveBooking}
                    onReject={handleRejectBooking}
                    onMessage={() => {
                        /* Messages now handled within modal */
                    }}
                    onUpdateTime={handleUpdateBookingTime}
                    onCancel={handleCancelBooking}
                    onMessagesRead={handleMessagesRead}
                    onReturnIssued={loadDashboardData}
                />
            )}
            {/* Rejection Modal */}
            {showRejectionModal && bookingToReject && (
                <RejectionModal
                    booking={bookingToReject}
                    onClose={() => {
                        setShowRejectionModal(false);
                        setBookingToReject(null);
                    }}
                    onConfirm={confirmRejectBooking}
                />
            )}
            {/* Cancellation Modal */}
            {showCancellationModal && bookingToCancel && (
                <CancellationModal
                    booking={bookingToCancel}
                    onClose={() => {
                        setShowCancellationModal(false);
                        setBookingToCancel(null);
                    }}
                    onConfirm={handleConfirmCancellation}
                />
            )}
            {/* Sick Day Modal */}
            {showSickDayModal && (
                <SickDayConfirmModal
                    isOpen={showSickDayModal}
                    onClose={() => setShowSickDayModal(false)}
                    onConfirm={handleConfirmSickDay}
                    onDateChange={dateIso => setTodaysBookings(getSickDayBookingsForDate(dateIso))}
                    todaysBookings={todaysBookings}
                    initialDate={getSickDayInitialDate()}
                    isProcessing={isSickDayProcessing}
                />
            )}
            {/* Confirm All Confirmation Modal (header "Confirm" button) */}
            <ConfirmationModal
                isOpen={!!confirmAllInfo}
                title={confirmAllInfo?.count === 1 ? "Confirm Booking?" : "Confirm Pending Bookings?"}
                message={
                    confirmAllInfo ? (
                        <>
                            {confirmAllInfo.count === 1
                                ? "Confirm this pending booking?"
                                : `Confirm all ${confirmAllInfo.count} pending bookings?`}
                            <span className="confirm-all-bookings-list">
                                {confirmAllInfo.bookings.map(b => (
                                    <span key={b.id} className="confirm-all-booking-item">
                                        <span className="confirm-all-booking-when">
                                            {DateTime.fromISO(b.bookingDate).toFormat("ccc d LLL")},{" "}
                                            {b.timeSlot?.startTime}-{b.timeSlot?.endTime}
                                        </span>
                                        <span className="confirm-all-booking-student">
                                            {b.studentName || "Student"}
                                        </span>
                                    </span>
                                ))}
                            </span>
                            {confirmAllInfo.count === 1 && confirmAllInfo.unpaidBeforeCount === 1
                                ? "Note: this booking is set to pay before the lesson and has not been paid yet. Confirming it means you will need to request payment from the student yourself."
                                : confirmAllInfo.unpaidBeforeCount === 1
                                ? "Note: 1 of these is set to pay before the lesson and has not been paid yet. Confirming it means you will need to request payment from that student yourself."
                                : confirmAllInfo.unpaidBeforeCount > 1
                                ? `Note: ${confirmAllInfo.unpaidBeforeCount} of these are set to pay before the lesson and have not been paid yet. Confirming them means you will need to request payment from those students yourself.`
                                : ""}
                        </>
                    ) : (
                        ""
                    )
                }
                confirmText={confirmAllInfo?.count === 1 ? "Confirm" : "Confirm All"}
                cancelText="Cancel"
                onConfirm={() => {
                    setConfirmAllInfo(null);
                    doConfirmAllPending();
                }}
                onCancel={() => setConfirmAllInfo(null)}
                variant="primary"
            />
            {/* Approve-before-payment warning: unpaid before-lesson bookings are normally
                confirmed by the student paying, so approval needs informed consent */}
            <ConfirmationModal
                isOpen={!!approveWarningBooking}
                title="Approve Before Payment?"
                message="This booking is set to pay before the lesson and the student has not paid yet. Approving will confirm the booking without payment, so you will need to collect payment yourself, for example with Request Payment. If you would rather not take payment upfront, you can change Payment Timing to after the lesson in your booking settings."
                confirmText="Approve Anyway"
                cancelText="Cancel"
                onConfirm={() => {
                    const idToApprove = approveWarningBooking.id;
                    setApproveWarningBooking(null);
                    doApproveBooking(idToApprove);
                }}
                onCancel={() => setApproveWarningBooking(null)}
                variant="warning"
            />
            {/* Instructor Dashboard Help Modal */}
            <InstructorDashboardHelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
        </div>
    );
};

export default InstructorDashboard;
